import React, { useEffect, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { makeRequest } from "../util/AxiosInstance";
import { CalStatus } from "../types/cal-status.type";
// Import Grid and motion
import { Button, Grid, IconButton, Paper, Skeleton, Typography, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import { motion } from "framer-motion";
import { LoadingButton } from "@mui/lab";
import useCalBooking from "../hooks/useCalBooking";
import { Setting } from "iconsax-react";
import { Title } from "@mui/icons-material";
import { TitlePlusText } from "../shared/TitlePlusText";
import { isEmpty } from "underscore";
import { margin } from "@mui/system";
import Placeholder from "./Placeholder";
import { appStore } from "../zustand";

const BookingItem = ({ booking }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [openCancelModal, setOpenCancelModal] = useState(false);
    const [openRescheduleModal, setOpenRescheduleModal] = useState(false);
    const [openApproveModal, setOpenApproveModal] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [rescheduleDate, setRescheduleDate] = useState('');
    const [rescheduleReason, setRescheduleReason] = useState('');
    const anchorRef = useRef(null);

    const { cancelBooking, rescheduleBooking, approveBooking } = useCalBooking();

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleCancelClick = () => {
        handleMenuClose();
        setOpenCancelModal(true);
    };

    const handleRescheduleClick = () => {
        handleMenuClose();
        setOpenRescheduleModal(true);
    };

    const handleApproveClick = () => {
        handleMenuClose();
        setOpenApproveModal(true);
    };

    const handleCancelSubmit = async () => {
        try {
            await cancelBooking(booking.uid, {
                cancellationReason: cancelReason,
                cancelSubsequentBookings: false
            });
            setOpenCancelModal(false);
            setCancelReason('');
            // Add any success notification here
        } catch (error) {
            // Add error handling here
            console.error(error);
        }
    };

    const handleRescheduleSubmit = async () => {
        try {
            await rescheduleBooking(booking.uid, {
                start: rescheduleDate,
                reschedulingReason: rescheduleReason
            });
            setOpenRescheduleModal(false);
            setRescheduleDate('');
            setRescheduleReason('');
            // Add any success notification here
        } catch (error) {
            // Add error handling here
            console.error(error);
        }
    };

    const handleApproveSubmit = async () => {
        try {
            await approveBooking(booking.uid);
            setOpenApproveModal(false);
            // Add any success notification here
        } catch (error) {
            // Add error handling here
            console.error(error);
        }
    };

    return (
        <>
            <Paper
                elevation={2}
                sx={{
                    p: 2,
                    mb: 2,
                    cursor: "pointer",
                    position: 'relative',
                    borderRadius: 4,
                }}
            >
                <Grid container spacing={0} gap={2} mb={2}>
                    <Grid item xs={10}>
                        <Typography variant="body1" component="h3" fontWeight={500} gutterBottom>
                            {booking.title}
                        </Typography>
                    </Grid>
                    <Grid item xs={1} sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: 'start',
                        ml:'auto'
                    }}>
                        <IconButton
                            ref={anchorRef}
                            onClick={handleMenuClick}
                            size="medium"
                        ><Setting /></IconButton>
                    </Grid>
                </Grid>
                <TitlePlusText style={{
                    marginTop: '1rem',
                }} title={'Description'} text={'description' in booking && !isEmpty(booking.description) ? booking.description : 'No description'} />
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TitlePlusText title="Start Time" text={new Date(booking.start).toLocaleTimeString()} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TitlePlusText title="End Time" text={new Date(booking.end).toLocaleTimeString()} />
                    </Grid>
                </Grid>
            </Paper>

            {/* Context Menu */}
            <Menu
                anchorEl={anchorRef.current}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        sx: {
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                            mt: 1.5,
                            '& .MuiAvatar-root': {
                                width: 32,
                                height: 32,
                                ml: -0.5,
                                mr: 1,
                            },
                            '&::before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                right: 14,
                                width: 10,
                                height: 10,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                            },
                        },
                    },
                }}
            >
                <MenuItem onClick={handleCancelClick}>Cancel Booking</MenuItem>
                <MenuItem onClick={handleRescheduleClick}>Reschedule</MenuItem>
                <MenuItem onClick={handleApproveClick}>Approve</MenuItem>
            </Menu>

            {/* Cancel Modal */}
            <Dialog open={openCancelModal} onClose={() => setOpenCancelModal(false)}>
                <DialogTitle>Cancel Booking</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Reason for Cancellation"
                        fullWidth
                        multiline
                        rows={4}
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCancelModal(false)}>Cancel</Button>
                    <Button onClick={handleCancelSubmit} variant="contained" color="error">
                        Confirm Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reschedule Modal */}
            <Dialog open={openRescheduleModal} onClose={() => setOpenRescheduleModal(false)}>
                <DialogTitle>Reschedule Booking</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="New Date & Time"
                        type="datetime-local"
                        fullWidth
                        value={rescheduleDate}
                        onChange={(e) => setRescheduleDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        margin="dense"
                        label="Reason for Rescheduling"
                        fullWidth
                        multiline
                        rows={4}
                        value={rescheduleReason}
                        onChange={(e) => setRescheduleReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenRescheduleModal(false)}>Cancel</Button>
                    <Button onClick={handleRescheduleSubmit} variant="contained" color="primary">
                        Confirm Reschedule
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Approve Modal */}
            <Dialog open={openApproveModal} onClose={() => setOpenApproveModal(false)}>
                <DialogTitle>Approve Booking</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to approve this booking?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenApproveModal(false)}>Cancel</Button>
                    <Button onClick={handleApproveSubmit} variant="contained" color="success">
                        Confirm Approve
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1 // Delay between each item animation
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.5
        }
    }
};

const Booking = ({ status = CalStatus.ALL }) => {
    const mode = appStore((state) => state.mode);
    const {
        data,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        isError
    } = useInfiniteQuery({
        queryKey: ['bookings', status],
        queryFn: async ({ pageParam = 1 }) => {
            const response = await makeRequest(
                "get",
                `/${mode == 'admin' ? 'admin' : 'cal'}/bookings?page=${pageParam}&status=${status}`
            );
            return response.data;
        },
        getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextPage : undefined),
    });

    useEffect(() => {
        if (error) {
            console.log('error', error)
        }
    }, [error, isError]);

    const allBookings = data?.pages.flatMap(page => page.data) ?? [];

    if (isLoading) {
        return (
            <Grid container spacing={2}>
                {[1, 2, 3, 4, 5, 6].map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item}>
                        <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 4 }} />
                    </Grid>
                ))}
            </Grid>
        );
    }

    if (!isLoading && !isError && !allBookings.length) {
        return <Placeholder text={'No bookings found'} />;
    }

    if (isError) {
        return <Typography color="error">Error loading bookings: {error.message}</Typography>;
    }

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            <Grid container spacing={2}>
                {allBookings.map((booking) => (
                    // Each booking is a motion Grid item
                    <Grid item xs={12} sm={6} md={4} key={booking.uid}>
                        <motion.div variants={itemVariants}>
                            <BookingItem booking={booking} />
                        </motion.div>
                    </Grid>
                ))}
            </Grid>
            {hasNextPage && (
                <LoadingButton
                    loading={isFetchingNextPage}
                    onClick={() => fetchNextPage()}
                    variant="outlined"
                    fullWidth
                >
                    Load More
                </LoadingButton>
            )}
        </motion.div>
    );
};

export default Booking;
